import { ANALYTICS_MAX_BATCH_EVENTS as L, createEventWithMetadata as H } from "@yujacorp/verity-api-contract/schemas/zod";
const T = {
  queueCapacity: 500,
  // The server rejects larger batches, so the flush size tracks the contract's ceiling.
  flushMaxEvents: L,
  flushLingerMs: 5e3,
  deliveryTimeoutMs: 3e4,
  giveUpAttempts: 10,
  giveUpAgeMs: 1440 * 60 * 1e3,
  backoffBaseMs: 5e3,
  backoffCapMs: 300 * 1e3,
  // 429 and 408 are carved out of the otherwise-permanent 4xx range: both mean "later", not "never".
  retryableHttpStatuses: /* @__PURE__ */ new Set([408, 429, 500, 502, 503, 504])
}, P = (n) => {
  if (typeof n != "object" || n === null) return;
  const t = n.data?.httpStatus;
  if (typeof t == "number") return t;
  const s = n.meta?.response?.status;
  if (typeof s == "number") return s;
  const o = n.status;
  return typeof o == "number" ? o : void 0;
}, _ = (n, t = T.retryableHttpStatuses) => {
  const s = P(n);
  return s === void 0 || t.has(s) ? "retryable" : "permanent";
}, I = (n, t) => Math.min(t.backoffBaseMs * 2 ** (n - 1), t.backoffCapMs) * (0.5 + Math.random()), z = () => {
  const n = /* @__PURE__ */ new Map();
  return {
    put: (t) => {
      for (const s of t) n.set(s.event.eventUuid, s);
      return Promise.resolve();
    },
    due: (t, s) => Promise.resolve(
      [...n.values()].filter((o) => o.nextAttemptAt <= t).sort((o, i) => o.enqueuedAt - i.enqueuedAt).slice(0, s)
    ),
    remove: (t) => {
      for (const s of t) n.delete(s);
      return Promise.resolve();
    },
    size: () => Promise.resolve(n.size)
  };
}, N = (n, t, s = {}) => {
  const o = H(n, t, {
    eventUuid: crypto.randomUUID(),
    timestamp: s.occurredAt ?? /* @__PURE__ */ new Date()
  });
  if (o.ok) return o.event;
  console.error("Analytics event could not be constructed", o);
}, y = (n, t) => console.error("Analytics event dropped", { action: n, reason: t }), j = (n, t = {}) => {
  const s = t.preEmitHook ?? ((e) => e), o = { ...T, ...t.config }, i = t.queueStore ?? z();
  let M = t.emissionEnabled ?? !0, h = !1, A = !1, l, f;
  const w = () => {
    l !== void 0 && (clearTimeout(l), l = void 0);
  }, U = () => {
    A || l !== void 0 || (l = setTimeout(() => {
      l = void 0, v();
    }, o.flushLingerMs));
  }, C = (e) => {
    A || f !== void 0 || (f = setTimeout(() => {
      f = void 0, v();
    }, e));
  }, S = async (e) => {
    try {
      return await s(e);
    } catch {
      return e;
    }
  }, k = async (e) => {
    const u = await Promise.all(e.map((c) => S(c.event))), p = new AbortController(), q = setTimeout(() => p.abort(), o.deliveryTimeoutMs);
    try {
      const c = await n(u, { signal: p.signal });
      for (const a of c.results) {
        if (a.status !== "rejected") continue;
        const d = e.find(({ event: m }) => m.eventUuid === a.eventUuid);
        y(d?.event.action ?? "unknown", a.reason);
      }
      return await i.remove(e.map(({ event: a }) => a.eventUuid)), "delivered";
    } catch (c) {
      if (_(c, o.retryableHttpStatuses) === "permanent") {
        for (const r of e) y(r.event.action, c);
        return await i.remove(e.map(({ event: r }) => r.eventUuid)), "delivered";
      }
      const a = Date.now(), d = [], m = [];
      for (const r of e) {
        const b = r.attemptCount + 1, D = a - r.enqueuedAt >= o.giveUpAgeMs;
        b >= o.giveUpAttempts || D ? m.push(r) : d.push({ ...r, attemptCount: b, nextAttemptAt: a + I(b, o) });
      }
      for (const r of m) y(r.event.action, c);
      return m.length > 0 && await i.remove(m.map(({ event: r }) => r.eventUuid)), d.length > 0 && (await i.put(d), C(Math.max(0, Math.min(...d.map((r) => r.nextAttemptAt)) - a))), "backoff";
    } finally {
      clearTimeout(q);
    }
  }, v = async () => {
    if (!(h || !M || A)) {
      h = !0;
      try {
        for (; ; ) {
          const e = await i.due(Date.now(), o.flushMaxEvents);
          if (e.length === 0 || (w(), await k(e) === "backoff")) return;
        }
      } finally {
        h = !1;
      }
    }
  }, x = async (e) => {
    if (await i.size() >= o.queueCapacity) {
      y(e.action, "queue capacity reached");
      return;
    }
    const u = Date.now();
    await i.put([{ event: e, attemptCount: 0, nextAttemptAt: 0, enqueuedAt: u }]), await i.size() >= o.flushMaxEvents ? v() : U();
  };
  let E = Promise.resolve();
  const g = (e) => {
    E = E.then(() => x(e)).catch((u) => y(e.action, u));
  };
  return {
    emit: (e, u) => {
      const p = N(e, u);
      p !== void 0 && g(p);
    },
    emitEvent: g,
    setEmissionEnabled: (e) => {
      M = e, e && v();
    },
    flush: v,
    close: () => {
      A = !0, w(), f !== void 0 && (clearTimeout(f), f = void 0);
    }
  };
};
export {
  T as DEFAULT_ANALYTICS_EMITTER_CONFIG,
  N as buildAnalyticsEvent,
  _ as classifyAnalyticsDeliveryFailure,
  j as createAnalyticsEmitter,
  z as createMemoryQueueStore
};
//# sourceMappingURL=client.js.map
